import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  Typography,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
} from "@mui/material";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";

import DailyAttendanceCard from "./DailyAttendanceCard";
import Footer from "./Footer";

import { downloadQrCode } from "../../../services/mentorServices/MentorServices";
import { getReportForMentorMobile } from "../../../services/reportServices/mentorReportServices/AttendanceReportServices";

const ErrorBoundary = ({ children, fallback }) => {
  const [hasError, setHasError] = useState(false);
  useEffect(() => {
    const errorHandler = () => setHasError(true);
    window.addEventListener("error", errorHandler);
    return () => window.removeEventListener("error", errorHandler);
  }, []);
  return hasError ? fallback : children;
};

const InfoRow = ({ label, value }) => (
  <Box display="flex" justifyContent="space-between">
    <Typography>{label}:</Typography>
    <Typography>{value}</Typography>
  </Box>
);


const AttendanceCard = ({ day, onOpenDialog }) => {
  const report = Array.isArray(day.attendanceReport) ? day.attendanceReport[0] : [];


  const {
    AttendanceDate,
    totalLearners,
    TotalPresentEngineer,
    totalAbsentEngrs,
    totalPresentEngrsPercent,
  } = report;

  return (
    <Card sx={{ p: 2, mb: 2, boxShadow: 3 }}>
      <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={1}>
        <Typography variant="h6" flex={1} minWidth={0}>{day?.attendanceName}</Typography>
        <Box display="flex" gap={1}>
          <Button variant="contained" size="small" onClick={() => onOpenDialog(day, "qr")}>QR</Button>
          <Button variant="contained" size="small" onClick={() => onOpenDialog(day, "view")}>View</Button>
        </Box>
      </Box>

      {day.attendanceReport?.length > 0 && (
        <Box mt={2} display="flex" flexDirection="column" gap={1}>
          <InfoRow label="Attendance Date" value={AttendanceDate} />
          <InfoRow label="Lab Size" value={totalLearners} />
          <InfoRow label="Attended" value={TotalPresentEngineer} />
          <InfoRow label="Not Attended" value={totalAbsentEngrs} />
          <InfoRow label="Attended %" value={`${Math.round(totalPresentEngrsPercent) || 0}%`} />
        </Box>
      )}
    </Card>
  );
};

const DailyAttendanceDashboard = () => {
  const location = useLocation();
  const { coeId, labId, labs } = location.state || {};

  const [selection, setSelection] = useState({
    coe: coeId || "",
    lab: labId || "",
  });

  const [dailyAttendances, setDailyAttendances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [dialogState, setDialogState] = useState({
    open: false,
    mode: null,
    selectedDay: null,
  });

  const [qrCode, setQrCode] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);

  const handleDialogOpen = (day, mode) => {
    setDialogState({ open: true, mode, selectedDay: day });
    if (mode === "qr") {
      setQrCode(null);
      setQrLoading(true);
    }
  };

  const handleDialogClose = () => {
    if (qrCode) {
      window.URL.revokeObjectURL(qrCode);
      setQrCode(null);
    }
    setDialogState({ open: false, mode: null, selectedDay: null });
    setQrLoading(false);
  };

  const fetchData = useCallback(async () => {
    const { coe, lab } = selection;
    if (!coe || !lab) {
      setError("Please select both COE and Lab.");
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await getReportForMentorMobile(lab);
      const data = response?.data?.data;
      if (!Array.isArray(data)) throw new Error("Invalid data format");
      setDailyAttendances(data);
      setError(null);
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to fetch daily attendance.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [selection]);

  useEffect(() => {
    if (selection.coe && selection.lab) {
      fetchData();
    }
  }, [fetchData]);

  useEffect(() => {
    if (!(dialogState.open && dialogState.mode === "qr" && dialogState.selectedDay)) return;

    const fetchQR = async () => {
      try {
        const response = await downloadQrCode(dialogState.selectedDay.dailyAttendanceId);
        const blob = new Blob([response.data], { type: "image/png" });
        const url = window.URL.createObjectURL(blob);
        setQrCode(url);
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || "Failed to fetch QR code");
      } finally {
        setQrLoading(false);
      }
    };

    fetchQR();

    return () => {
      if (qrCode) window.URL.revokeObjectURL(qrCode);
    };
  }, [dialogState]);

  const sortedAttendances = useMemo(() => (
    [...dailyAttendances].sort(
      (a, b) => new Date(b.AttendanceDate) - new Date(a.AttendanceDate)
    )
  ), [dailyAttendances]);

  return (
    <ErrorBoundary fallback={<Typography color="error">Something went wrong. Please try again.</Typography>}>
      <FormControl size="small" sx={{ width: "100%", mb: 2 }}>
        <InputLabel id="lab-select-label">Lab Name*</InputLabel>
        <Select
          labelId="lab-select-label"
          id="lab-select"
          value={selection.lab}
          onChange={(e) => setSelection((prev) => ({ ...prev, lab: e.target.value }))}
          label="Lab Name"
        >
          {labs?.map((lab) => (
            <MenuItem key={lab.id} value={lab.id}>{lab.lab_name}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <Typography variant="h6" align="center" fontWeight="bold" mb={2}>
        Daily Attendance Dashboard
      </Typography>

      <Box display="flex" flexDirection="column" gap={2} mb={10}>
        {loading ? (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : sortedAttendances.length ? (
          sortedAttendances.map((day, index) => (
            <AttendanceCard key={index} day={day} onOpenDialog={handleDialogOpen} />
          ))
        ) : (
          <Typography>No daily attendance records found.</Typography>
        )}
      </Box>

      <Dialog open={dialogState.open} onClose={handleDialogClose} fullWidth maxWidth="sm">
        <DialogContent sx={{ p: 0 }}>
          {dialogState.mode === "view" && dialogState.selectedDay && (
            <DailyAttendanceCard day={dialogState.selectedDay} />
          )}
          {dialogState.mode === "qr" && (
            qrLoading ? (
              <Box display="flex" justifyContent="center" p={2}><CircularProgress /></Box>
            ) : qrCode ? (
              <img src={qrCode} alt="QR Code" style={{ width: "100%" }} />
            ) : (
              <Typography p={2}>No QR code available</Typography>
            )
          )}
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button onClick={handleDialogClose} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>

      <Footer coeId={selection.coe} labId={selection.lab} labs={labs} />
    </ErrorBoundary>
  );
};

export default DailyAttendanceDashboard;
